import { createRouter, createWebHistory } from 'vue-router'
import { useConfigStore } from '@/stores/configStore'
import { useLanguage } from '@/composables/useLanguage'
import MainLayout from '../layouts/MainLayout.vue'
import AuthLayout from '../layouts/AuthLayout.vue'
import HomePage from '../pages/HomePage.vue'
import TShirtPage from '../pages/TShirtPage.vue'
import CartPage from '../pages/CartPage.vue'
import AccountPage from '../pages/AccountPage.vue'
import AssistancePage from '../pages/AssistancePage.vue'
import ContactPage from '../pages/ContactPage.vue'
import PrivacyPage from '../pages/PrivacyPage.vue'
import CookiePage from '../pages/CookiePage.vue'
import TermsPage from '../pages/TermsPage.vue'
import AboutPage from '../pages/AboutPage.vue'
import LoginPage from '../pages/LoginPage.vue'
import RegisterPage from '../pages/RegisterPage.vue'
import RegisterSuccessPage from '../pages/RegisterSuccessPage.vue'
import ForgotPasswordPage from '../pages/ForgotPasswordPage.vue'
import ForgotPasswordSuccessPage from '../pages/ForgotPasswordSuccessPage.vue'
import ResetPasswordPage from '../pages/ResetPasswordPage.vue'
import VerifyEmailPage from '../pages/VerifyEmailPage.vue'
import VerifyNewsletterPage from '../pages/VerifyNewsletterPage.vue'
import UnsubscribePage from '../pages/UnsubscribePage.vue'
import CountdownPage from '../pages/CountdownPage.vue'
import CheckoutPage from '../pages/CheckoutPage.vue'
import CheckoutSuccessPage from '../pages/CheckoutSuccessPage.vue'
import PaymentPage from '../pages/PaymentPage.vue'
import OrderDetailsPage from '../pages/OrderDetailsPage.vue'

const routes = [
  {
    path: '/',
    component: MainLayout,
    children: [
      {
        path: '',
        name: 'Home',
        component: HomePage
      },
      {
        path: 'countdown',
        name: 'Countdown',
        component: CountdownPage
      },
      {
        path: 't-shirt',
        redirect: '/t-shirt-streetwear-uomo'
      },
      {
        path: 'carrello',
        name: 'Cart',
        component: CartPage
      },
      {
        path: 'checkout',
        name: 'Checkout',
        component: CheckoutPage
      },
      {
        path: 'checkout/success',
        name: 'CheckoutSuccess',
        component: CheckoutSuccessPage
      },
      {
        path: 'track/:token',
        name: 'GuestTracking',
        component: () => import('../pages/GuestOrderTracking.vue')
      },
      {
        path: 'payment',
        name: 'Payment',
        component: PaymentPage
      },
      {
        path: 'account',
        name: 'Account',
        component: AccountPage
      },
      {
        path: 'account/ordini/:id',
        name: 'OrderDetail',
        component: OrderDetailsPage
      },
      {
        path: 'assistenza',
        alias: ['spedizioni', 'resi-cambi', 'pagamenti', 'faq'],
        name: 'Assistance',
        component: AssistancePage
      },
      {
        path: 'contatti',
        name: 'Contact',
        component: ContactPage
      },
      {
        path: 'privacy',
        alias: ['privacy-policy'],
        name: 'PrivacyPolicy',
        component: PrivacyPage
      },
      {
        path: 'cookie',
        alias: ['cookie-policy'],
        name: 'CookiePolicy',
        component: CookiePage
      },
      {
        path: 'termini',
        alias: ['termini-condizioni'],
        name: 'Terms',
        component: TermsPage
      },
      {
        path: 'chi-siamo',
        name: 'About',
        component: AboutPage
      },
      // Nuova rotta dinamica per le categorie del DB (messa alla fine per precedenza)
      {
        path: ':categorySlug',
        name: 'CategoryPage',
        component: TShirtPage
      }
    ]
  },
  {
    path: '/reset-password',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'ResetPassword',
        component: ResetPasswordPage
      }
    ]
  },
  {
    path: '/verify-email',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'VerifyEmail',
        component: VerifyEmailPage
      }
    ]
  },
  {
    path: '/verify-newsletter',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'VerifyNewsletter',
        component: VerifyNewsletterPage
      }
    ]
  },
  {
    path: '/unsubscribe',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'Unsubscribe',
        component: UnsubscribePage
      }
    ]
  },
  {
    path: '/login',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'Login',
        component: LoginPage
      }
    ]
  },
  {
    path: '/register',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'Register',
        component: RegisterPage
      }
    ]
  },
  {
    path: '/register-success',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'RegisterSuccess',
        component: RegisterSuccessPage
      }
    ]
  },
  {
    path: '/forgot-password',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'ForgotPassword',
        component: ForgotPasswordPage
      },
      {
        path: 'success',
        name: 'ForgotPasswordSuccess',
        component: ForgotPasswordSuccessPage
      }
    ]
  },

]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

router.beforeEach((to, from, next) => {
  const configStore = useConfigStore()
  const { t, currentLanguage } = useLanguage()

  // Gestione dinamica dei Meta Tag SEO e Social
  const baseTitle = 'Billy B — Streetwear Premium Milano'
  let pageTitle = to.name ? `${to.name} | ${baseTitle}` : baseTitle
  let description = 'Billy B è un brand indipendente di streetwear premium Made in Italy. Scopri collezioni in drop esclusivi e produzioni limitate.'
  let ogTitle = pageTitle
  let ogDescription = description
  const canonicalUrl = `https://billyb.it${to.path}`

  // Override specifici per la pagina Countdown
  if (to.name === 'Countdown') {
    pageTitle = t('seo.countdown.title')
    description = t('seo.countdown.description')
    ogTitle = t('seo.countdown.og_title')
    ogDescription = t('seo.countdown.og_description')
  }

  // Aggiornamento DOM
  document.title = pageTitle
  
  // Meta Description
  const metaDescription = document.querySelector('meta[name="description"]')
  if (metaDescription) metaDescription.setAttribute('content', description)

  // Open Graph
  const metaOgTitle = document.querySelector('meta[property="og:title"]')
  if (metaOgTitle) metaOgTitle.setAttribute('content', ogTitle)

  const metaOgDescription = document.querySelector('meta[property="og:description"]')
  if (metaOgDescription) metaOgDescription.setAttribute('content', ogDescription)

  const metaOgUrl = document.querySelector('meta[property="og:url"]')
  if (metaOgUrl) metaOgUrl.setAttribute('content', canonicalUrl)

  // Twitter
  const metaTwitterTitle = document.querySelector('meta[name="twitter:title"]')
  if (metaTwitterTitle) metaTwitterTitle.setAttribute('content', ogTitle)

  // Canonical
  let linkCanonical = document.querySelector('link[rel="canonical"]')
  if (linkCanonical) {
    linkCanonical.setAttribute('href', canonicalUrl)
  }

  // Hreflang (Esempio semplificato: gestisce it ed en)
  const languages = ['it', 'en', 'x-default']
  languages.forEach(lang => {
    let linkHreflang = document.querySelector(`link[hreflang="${lang}"]`)
    if (linkHreflang) {
      linkHreflang.setAttribute('href', canonicalUrl)
    }
  })

  // Se isCountdownMode è true, permettiamo solo le rotte necessarie (Landing e Legali)
  const allowedRoutesDuringCountdown = [
    'Countdown', 
    'VerifyEmail', 
    'VerifyNewsletter',
    'Unsubscribe',
    'PrivacyPolicy', 
    'CookiePolicy'
  ]
  
  if (configStore.isCountdownMode && !allowedRoutesDuringCountdown.includes(to.name)) {
    next({ name: 'Countdown' })
  } else {
    next()
  }
})

export default router
